import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { Heading } from 'rebass/styled-components';

import { SlidePanel, Loader, FormGroup } from 'ui/patterns';
import {
  Button,
  Stack,
  Separator,
  VisuallyHidden,
  StyledFormInput,
} from 'ui/components';
import { ProcedureAccordion } from 'ui/patterns';
import { ReactSelect } from 'ui/patterns/reactSelect';
import { EmptyState } from 'ui/patterns';

import {
  useGetExistingTemplates,
  useDeleteTemplate,
  useEditTemplateName,
  useSaveTemplate,
  useUpdateTemplate,
  useDeleteTemplateGroup,
  useEditTemplateGroupName,
} from 'hooks';

import * as Icon from 'assets/icons';
import NoDataImage from 'assets/images/NoDataImage';

import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { confirmAlert } from 'utils/confirm-alert';

import { BlobType } from 'types/procedureBuilder';
import { ExistingTableProps } from './existingTableTypes';
import { Group } from '../procedureTypes';
import { ErrorModal } from 'types/modalTypes';

function ExistingTable({
  accountId,
  showPanel,
  togglePanel: toggle,
}: ExistingTableProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedTable, setSelectedTable] = useState('');
  const [insertDisable, setInsertDisable] = useState(false);

  /*Grouping */
  const [isGroupEdited, setIsGroupEdited] = useState(false); // to handle changes in dropdown after group name change
  const [editGroupName, setEditGroupName] = useState(false); // group name edit state
  const [groupName, setGroupName] = useState<string>('All'); // group name & header
  const [selectedGroupValue, setSelectedGroupValue] = useState<Group>({
    label: ' All',
    value: 'All',
  }); // drop down selection
  const [selectedGroupTableList, setSelectedGroupTableList] = useState<
    Array<BlobType>
  >([]); //selected list
  /*Grouping */

  const { mutate: deleteTemplate, isLoading: isDeleteTemplateLoading } =
    useDeleteTemplate();

  const { mutate: editTemplateName, isLoading: isEditTemplateNameLoading } =
    useEditTemplateName();

  const { mutate: deleteGroup, isLoading: isDeleteTableGroupLoading } =
    useDeleteTemplateGroup();

  const { mutate: editGroupTitle, isLoading: isEditGroupTitleLoading } =
    useEditTemplateGroupName();

  const { data: tableData, isLoading: tableDataLoading } =
    useGetExistingTemplates({ showPanel, accountId, templateType: 'tables' });

  const { mutate: saveTemplate, isLoading: isSaveTemplateLoading } =
    useSaveTemplate();

  const { mutate: updateTemplate, isLoading: isUpdateTemplateLoading } =
    useUpdateTemplate();

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  const folderOptions = useMemo(() => {
    return (
      tableData &&
      tableData.Data &&
      tableData.Data[0]?.Blobs?.filter(
        (blob: BlobType) => blob.Type === 'folder',
      ).map((group: BlobType) => {
        return { label: group.Name, value: group.Name };
      })
    );
  }, [tableData]);

  const tableTemplateData = useMemo(() => {
    return tableData && tableData.Data && tableData.Data[0]?.Blobs;
  }, [tableData]);

  //handing group changes in dropdown
  useEffect(() => {
    if (tableTemplateData) {
      if (selectedGroupTableList.length >= 0 && selectedGroupValue !== null) {
        if (tableTemplateData.length !== 0) {
          const temp: Array<BlobType> = [];
          if (selectedGroupValue.value === 'All') {
            tableTemplateData?.map((tables: BlobType) => {
              if (tables.Type === 'html') {
                temp.push(tables);
              } else if (selectedGroupValue.value === tables.Name) {
                tables?.Blobs?.map((item: BlobType) => {
                  temp.push(item);
                });
              }
              setSelectedGroupTableList(temp);
            });
          } else {
            tableTemplateData?.map((tables: BlobType) => {
              if (selectedGroupValue.value === tables.Name) {
                tables?.Blobs?.map((item: BlobType) => {
                  temp.push(item);
                });
                setSelectedGroupTableList(temp);
              }
            });
          }
        } else {
          setSelectedGroupTableList([]);
        }
      }
    }
  }, [
    folderOptions,
    selectedGroupTableList.length,
    selectedGroupValue,
    tableTemplateData,
  ]);

  //reset header and dropdown selection to All after deleting group or last entry
  useEffect(() => {
    const value = folderOptions?.filter(
      (item: { label: string; value: string }) =>
        item.label === selectedGroupValue.label,
    );
    if (
      value?.length === 0 &&
      selectedGroupValue.label !== 'All' &&
      !isGroupEdited
    ) {
      setGroupName('All');
      setSelectedGroupValue({ label: 'All', value: 'All' });
    }
  }, [folderOptions, isGroupEdited, selectedGroupValue.label]);

  const updatedGroups = useMemo(() => {
    return tableTemplateData?.map((template: any) => {
      if (template.Type === 'html') {
        template.mappedGroups = [];
      }
      return template;
    });
  }, [tableTemplateData]);

  const mappedGrouping = () => {
    const ungrouped = updatedGroups?.filter(item => item.Type === 'html');
    const grouped = updatedGroups?.filter(item => item.Type !== 'html');
    if (grouped && ungrouped) {
      for (let j = 0; j < ungrouped.length; j++) {
        for (let i = 0; i < grouped?.length; i++) {
          grouped[i]?.Blobs?.map((subItem: any) => {
            if (ungrouped[j].Name === subItem.Name) {
              if (!ungrouped[j]?.mappedGroups?.length)
                ungrouped[j]?.mappedGroups?.push(grouped[i]?.Name);
              else {
                const isGroupPresent = ungrouped[j]?.mappedGroups?.filter(
                  (item: any) => item === grouped[i]?.Name,
                );
                if (!isGroupPresent?.length)
                  ungrouped[j]?.mappedGroups?.push(grouped[i]?.Name);
              }
            }
          });
        }
      }
    }
  };

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  const insertTable = () => {
    let table: Array<BlobType> | undefined = [];
    if (selectedGroupValue.value !== 'All') {
      const tableGroup = tableTemplateData?.filter(
        data =>
          data.Type === 'folder' && data.Name === selectedGroupValue.label,
      );
      table =
        tableGroup &&
        tableGroup[0]?.Blobs?.filter(
          (blob: BlobType) => blob.Name === selectedTable,
        );
    } else {
      table = tableTemplateData
        ?.filter(data => data.Type === 'html')
        ?.filter(item => item.Name === selectedTable);
    }
    const content = table && table[0]?.Content;
    const parser = new DOMParser();
    if (content) {
      const document = parser.parseFromString(content, 'text/html');
      const docTable = document.querySelector('body table:first-of-type');
      const docTableToString = docTable?.outerHTML;
      (window as any)?.tinymce?.activeEditor?.insertContent(docTableToString);
    }
  };

  //Table deletion affects only the respective folder
  const deleteTable = (fileTitle: string) => {
    let isDeleteFromAllFolder: boolean;
    confirmAlert({
      message:
        selectedGroupValue.label === 'All'
          ? 'Are you sure you want to delete this table?'
          : selectedGroupTableList.length === 1
          ? `As you are removing the last table of group [${groupName}], it will be removed from the list of groups. Are you sure you want to remove this table from the group?`
          : 'Are you sure you want to remove this table from the group?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            if (groupName !== 'All') {
              fileTitle = `${groupName}/${fileTitle}`;
              isDeleteFromAllFolder = false;
            } else isDeleteFromAllFolder = true;
            if (fileTitle) {
              deleteTemplate(
                {
                  accountId,
                  params: {
                    Container: 'tables',
                    Title: fileTitle,
                    IsDeleteFromAllFolder: isDeleteFromAllFolder,
                  },
                },
                {
                  onSuccess: () => {
                    setIsGroupEdited(false);
                    showSuccessToast({
                      message: 'Your changes were saved successfully.',
                    });
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({ message: `${error.Errors[0].Message}` });
                  },
                },
              );
            }
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  //Table name edit affects all tables with same name
  const editTableTitle = (oldFileName: string, newFileName: string) => {
    let newTableName = '';
    if (oldFileName !== newFileName) {
      if (!newFileName.includes('.html'))
        newTableName = `${newFileName.trim()}.html`;
      else newTableName = newFileName.trim();
      editTemplateName(
        {
          params: {
            RootContainer: 'tables',
            ExistingFileName: oldFileName,
            NewlySuggestedFileName: newTableName,
          },
          accountId: accountId,
        },
        {
          onSuccess: () => {
            showSuccessToast({
              message: 'Your changes were saved successfully.',
            });
          },
          onError: (error: ErrorModal) => {
            showErrorToast({ message: `${error.Errors[0].Message}` });
          },
        },
      );
    }
  };

  const procedureActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          setSelectedTable('');
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => {
          insertTable();
          togglePanel();
          setSelectedTable('');
        }}
        disabled={selectedTable === '' || insertDisable === true ? true : false}
      >
        Insert
      </Button>
    </>
  );

  const existingTableCheck = (title: string | undefined) => {
    let checkedValue: Array<any> | undefined = [];
    const check = title?.toLowerCase().includes('.html');
    let value: string | undefined;
    if (check) value = title?.split('.')[0]?.trim();
    else value = title?.trim();
    const tableGroup = tableTemplateData?.filter(
      (data: BlobType) => data.Type === 'html',
    );
    checkedValue = tableGroup?.filter((item: any) => {
      if (item.Name.split('.')[0] === value) return item;
    });
    if (checkedValue?.length) {
      return true;
    } else return false;
  };

  const updateTable = (
    title: string,
    content: string | undefined,
    accountId: number,
  ) => {
    updateTemplate(
      {
        params: {
          Name: title.trim(),
          Content: content || '',
          ContainerName: 'tables',
        },
        accountId: accountId,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: 'Table saved successfully' });
        },
      },
    );
  };

  const deleteTableGroup = (title: string) => {
    setIsGroupEdited(true);
    confirmAlert({
      message: 'Are you sure you want to delete the group?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            if (title) {
              deleteGroup(
                {
                  accountId,
                  params: {
                    Container: 'tables',
                    Title: title,
                  },
                },
                {
                  onSuccess: () => {
                    setIsGroupEdited(false);
                    showSuccessToast({
                      message: 'Your changes were saved successfully.',
                    });
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({ message: `${error.Errors[0].Message}` });
                  },
                },
              );
            }
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  const handleGroupChange = (e: { label: string; value: string }) => {
    setIsGroupEdited(false);
    setSelectedGroupValue(e);
    setGroupName(e.value);
  };

  const handleGroupInsertion = (
    selectedGroups: Array<Group>,
    title: string,
    content: string | undefined,
    unselectedGroups: Array<Group>,
  ) => {
    const insertTitleTemp = selectedGroups.map((group: Group) => {
      return `${group.label}/${title}`;
    });
    const insertTitleString = insertTitleTemp.join(',');
    const removeTitleTemp = unselectedGroups?.map((group: Group) => {
      return `${group.label}/${title}`;
    });
    const removeTitleString = removeTitleTemp.join(',');
    saveTemplate({
      params: {
        Name: insertTitleString.trim(),
        Content: content || '',
        ContainerName: 'tables',
      },
      accountId: accountId,
    });
    if (unselectedGroups.length !== 0) {
      deleteTemplate(
        {
          accountId,
          params: {
            Container: 'tables',
            Title: removeTitleString.trim(),
            IsDeleteFromAllFolder: false,
          },
        },

        {
          onSuccess: () => {
            setIsGroupEdited(false);
          },
          onError: (error: any) => {
            showErrorToast({ message: `${error.message}` });
          },
        },
      );
    }
  };

  const handleTableGroupNameEdit = (
    oldGroupName: string,
    newGroupName: string,
  ) => {
    const isExists = folderOptions?.filter(
      (item: any) => item.label === newGroupName,
    );
    if (isExists?.length) {
      showErrorToast({
        message: 'Group name already exists.Please enter a different name',
      });
    }
    editGroupTitle(
      {
        params: {
          RootContainer: 'tables',
          ExistingDirectoryName: oldGroupName,
          NewlySuggestedDirectoryName: newGroupName.trim(),
        },
        accountId: accountId,
      },
      {
        onSuccess: (message: any) => {
          if (typeof message === 'string') {
            showErrorToast({ message: `${message}` });
          } else {
            setIsGroupEdited(true);
            setSelectedGroupValue({
              label: `${newGroupName.trim()}`,
              value: `${newGroupName.trim()}`,
            });
            showSuccessToast({
              message: 'Your changes were saved successfully.',
            });
          }
        },
        onError: (error: any) => {
          showErrorToast({ message: `${error.message}` });
        },
      },
    );
  };

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        togglePanel();
        setSelectedTable('');
      }}
      size="md"
      title="Select Table"
      actions={procedureActions()}
    >
      <FormGroup>
        <ReactSelect
          name="tableOptions"
          label="Group"
          isSearchable={true}
          isClearable={false}
          options={
            folderOptions !== undefined
              ? [{ label: 'All', value: 'All' }, ...folderOptions]
              : []
          }
          value={selectedGroupValue}
          classNamePrefix="pr-select"
          onChange={(e: any) => handleGroupChange(e)}
          onMenuOpen={() => setSelectedTable('')}
        />
      </FormGroup>
      <Separator direction="x" my={5} />
      <Stack direction="x" gap={1} alignItems="center" mb={3}>
        {editGroupName ? (
          <Stack direction="x" gap={1} alignItems="center" mb={3}>
            <StyledFormInput
              placeholder="Group name"
              style={{ paddingInline: '0.75rem', width: '300px' }}
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconBefore={<Icon.Check />}
              title="Save"
              onClick={() => {
                if (
                  !groupName ||
                  !groupName.trim() ||
                  groupName.toLowerCase() === 'all' ||
                  groupName.includes(',')
                ) {
                  showErrorToast({
                    message: 'Please enter a valid name',
                  });
                  return;
                } else {
                  setEditGroupName(false);
                  handleTableGroupNameEdit(selectedGroupValue.label, groupName);
                }
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              iconBefore={<Icon.X />}
              title="Cancel"
              onClick={() => {
                setEditGroupName(false);
                setGroupName(selectedGroupValue.label);
              }}
            />
          </Stack>
        ) : (
          <>
            <Heading as="h3" fontSize={3} fontWeight="medium">
              {groupName}
            </Heading>
            {groupName.toLowerCase() === 'all' ? null : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Edit group name"
                  onClick={() => setEditGroupName(true)}
                >
                  <Icon.Edit ariaHidden="true" />
                  <VisuallyHidden>Edit Group Name</VisuallyHidden>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Remove group"
                  onClick={() => deleteTableGroup(groupName)}
                >
                  <Icon.Delete />
                  <VisuallyHidden>Remove Group</VisuallyHidden>
                </Button>
              </>
            )}
          </>
        )}
      </Stack>
      <Stack direction="y" gap={3}>
        {selectedGroupTableList?.map((table, i) => (
          <ProcedureAccordion
            key={i}
            accountId={accountId}
            title={table.Name}
            content={table.Content}
            open={selectedTable}
            container="tables"
            setOpen={setSelectedTable}
            deleteTemplate={deleteTable}
            editTemplate={editTableTitle}
            templateExistingCheck={existingTableCheck}
            updateTemplate={updateTable}
            setInsertDisable={setInsertDisable}
            templateGroups={folderOptions}
            handleGroupInsertion={handleGroupInsertion}
            mappedGrouping={mappedGrouping}
            ungroupedSet={updatedGroups?.filter(
              (item: any) => item.Type === 'html',
            )}
            selectedGroupDropDown={selectedGroupValue}
          />
        ))}
      </Stack>
      {selectedGroupTableList?.length === 0 ? (
        <EmptyState>
          <NoDataImage feedback="No Tables Available" />
        </EmptyState>
      ) : null}
      {(tableDataLoading ||
        isDeleteTemplateLoading ||
        isEditTemplateNameLoading ||
        isDeleteTableGroupLoading ||
        isSaveTemplateLoading ||
        isUpdateTemplateLoading ||
        isEditGroupTitleLoading) && <Loader />}
    </SlidePanel>
  );
}

export default ExistingTable;
